import { getDriverDetails, getDrivers } from "@/features/drivers/api/driversApi";

import type {
    RoadWorthinessApprovalStatus,
    DriversQueryParams,
} from "@/features/drivers/types";

import type {
    RoadWorthinessQueueItem,
    RoadWorthinessQueueParams,
    RoadWorthinessQueueResponse,
    RoadWorthinessQueueStatus,
} from "../types";

const SOURCE_PAGE_SIZE = 25;

const normalizeApprovalStatus = (
    status: RoadWorthinessApprovalStatus | null
): RoadWorthinessQueueStatus => {
    if (status === "APPROVED" || status === "REJECTED") return status;
    return "PENDING";
};

const mapRoadWorthinessItem = (
    detail: Awaited<ReturnType<typeof getDriverDetails>>["data"],
    driverId: string
) => {
    const driverProfile = detail?.driverProfile;
    if (!driverProfile) return null;

    const roadWorthiness = driverProfile.roadWorthiness;
    if (!roadWorthiness) return null;

    return {
        driverId,
        firstName: detail.firstName,
        lastName: detail.lastName,
        email: detail.email,
        phoneNumber: detail.phoneNumber,
        driverStatus: detail.status,
        certificateNumber: roadWorthiness.certificateNumber,
        documentUrl: roadWorthiness.documentUrl,
        issueDate: roadWorthiness.issueDate,
        expiryDate: roadWorthiness.expiryDate,
        isValid: roadWorthiness.isValid,
        approvalStatus: normalizeApprovalStatus(roadWorthiness.approvalStatus),
        createdAt: detail.createdAt,
        updatedAt: detail.updatedAt,
    } satisfies RoadWorthinessQueueItem;
};

const approvalPriority: Record<RoadWorthinessQueueStatus, number> = {
    PENDING: 0,
    REJECTED: 1,
    APPROVED: 2,
};

const compareRoadWorthinessItems = (
    left: RoadWorthinessQueueItem,
    right: RoadWorthinessQueueItem
) => {
    const statusDiff =
        approvalPriority[left.approvalStatus] - approvalPriority[right.approvalStatus];
    if (statusDiff !== 0) return statusDiff;

    const validityDiff = Number(left.isValid) - Number(right.isValid);
    if (validityDiff !== 0) return validityDiff;

    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
};

export const getRoadWorthinessQueue = async (
    params: RoadWorthinessQueueParams = {}
) => {
    const page = params.page ?? 0;
    const size = params.size ?? 20;
    const search = params.search?.trim() || undefined;
    const requestedStatus = params.approvalStatus || "";

    const aggregated: RoadWorthinessQueueItem[] = [];
    let sourcePage = 0;
    let totalSourcePages = 0;

    do {
        const driverQuery: DriversQueryParams = {
            page: sourcePage,
            size: SOURCE_PAGE_SIZE,
            search,
        };

        const driverPage = await getDrivers(driverQuery);
        totalSourcePages = driverPage.data.totalPages;

        const detailResults = await Promise.allSettled(
            driverPage.data.drivers.map((driver) => getDriverDetails(driver.userId))
        );

        detailResults.forEach((result, index) => {
            if (result.status !== "fulfilled") return;

            const sourceDriver = driverPage.data.drivers[index];
            if (!sourceDriver) return;

            const queueItem = mapRoadWorthinessItem(
                result.value.data,
                sourceDriver.userId
            );
            if (!queueItem) return;

            if (requestedStatus && queueItem.approvalStatus !== requestedStatus) return;

            aggregated.push(queueItem);
        });

        sourcePage += 1;
    } while (sourcePage < totalSourcePages);

    aggregated.sort(compareRoadWorthinessItems);

    const totalElements = aggregated.length;
    const totalPages = Math.max(1, Math.ceil(totalElements / size));
    const start = page * size;
    const end = start + size;

    return {
        items: aggregated.slice(start, end),
        page,
        size,
        totalElements,
        totalPages,
        hasNext: end < totalElements,
        hasPrevious: page > 0,
    } satisfies RoadWorthinessQueueResponse;
};
